import React from 'react';
import Icon from '../../../../components/AppIcon';

const NameFormatHelper = ({ firstName, lastName, countryCode }) => {
    const getNameFormatGuidance = (countryCode) => {
        // Different naming conventions by country/region
        const conventions = {
            'JP': {
                message: 'In Japan, family name typically comes first',
                suggestion: 'Consider if you entered names in the correct order'
            },
            'KR': {
                message: 'In Korea, family name typically comes first',
                suggestion: 'Consider if you entered names in the correct order'
            },
            'CN': {
                message: 'In China, family name typically comes first',
                suggestion: 'Consider if you entered names in the correct order'
            },
            'IS': {
                message: 'In Iceland, patronymic naming is common',
                suggestion: 'Your last name might be based on your father\'s name'
            },
            'ES': {
                message: 'In Spain, two surnames are typically used',
                suggestion: 'You can include both surnames in the last name field'
            },
            'PT': {
                message: 'In Portugal, multiple surnames are common',
                suggestion: 'You can include all surnames in the last name field'
            },
            'BR': {
                message: 'In Brazil, multiple surnames are common',
                suggestion: 'You can include all surnames in the last name field'
            }
        };

        return conventions[countryCode] || null;
    };

    const validateNameLength = (firstName, lastName) => {
        const issues = [];

        if (firstName && firstName.length < 2) {
            issues.push('First name seems too short');
        }

        if (lastName && lastName.length < 2) {
            issues.push('Last name seems too short');
        }

        if (firstName && firstName.length > 50) {
            issues.push('First name seems too long');
        }

        if (lastName && lastName.length > 50) {
            issues.push('Last name seems too long');
        }

        return issues;
    };

    const detectSpecialCharacters = (firstName, lastName) => {
        const specialCharRegex = /[^a-zA-Z\s\-'\.]/;
        const issues = [];

        if (firstName && specialCharRegex.test(firstName)) {
            issues.push('First name contains unusual characters');
        }

        if (lastName && specialCharRegex.test(lastName)) {
            issues.push('Last name contains unusual characters');
        }

        return issues;
    };

    if (!firstName && !lastName) return null;

    const guidance = getNameFormatGuidance(countryCode);
    const lengthIssues = validateNameLength(firstName, lastName);
    const charIssues = detectSpecialCharacters(firstName, lastName);
    const allIssues = [...lengthIssues, ...charIssues];

    // Don't show anything if names look fine
    if (!guidance && allIssues.length === 0) return null;

    return (
        <div className="mt-2 space-y-2">
            {/* Cultural naming guidance */}
            {guidance && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start">
                        <Icon name="info" className="w-4 h-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div className="text-sm">
                            <p className="text-blue-800 font-medium">{guidance.message}</p>
                            <p className="text-blue-600 mt-1">{guidance.suggestion}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Validation issues */}
            {allIssues.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-start">
                        <Icon name="alert-triangle" className="w-4 h-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div className="text-sm">
                            <p className="text-yellow-800 font-medium">Name format suggestions:</p>
                            <ul className="text-yellow-700 mt-1 space-y-1">
                                {allIssues.map((issue, index) => (
                                    <li key={index}>• {issue}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* General helpful tip */}
            {firstName && lastName && allIssues.length === 0 && !guidance && (
                <div className="text-xs text-accent-500">
                    💡 Make sure your name matches your official documents for verification
                </div>
            )}
        </div>
    );
};

export default NameFormatHelper; 